import { Close as CloseIcon, Launch } from "@mui/icons-material";
import { Button, Dialog, DialogContent, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { isEmpty } from "lodash";
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ELEMENT_TYPE } from "../../../constants";
import { LinkU } from "../../../icons";
import { setSimpleValue } from "../../../store/actions";
import { currentElementSelector, simpleValuesSelector } from "../../../store/selectors";
import objectDifference from "../../../utils/objectDifference";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  paper: {
    maxWidth: false,
    width: "90vw",
    height: "90vh",
    boxShadow: theme.shadows[5],
    backgroundColor: "#FFFFFF",
    padding: "0",
    paddingTop: "0 !important",
    display: "flex",
    "@media (max-width:1200px)": {
      width: "100vw",
    },
    "@media (max-width:650px) and (orientation: portrait)": {
      flexDirection: "column",
      justifyContent: "flex-start",
    },
  },
  mediaPreview: {
    flex: "2",
    margin: "0",
    border: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "column",
    "@media (max-width:960px)": {
      flex: "1",
    },
    "@media (max-width:650px) and (orientation: portrait)": {
      borderRight: "none",
      borderBottom: "1px solid #f5f5f5",
    },
  },
  mediaDetails: {
    flex: "1",
    // padding: '10px',
    // overflowY: 'auto',
    position: "relative",
    overflow: "hidden",
    paddingBottom: "35px",
    "@media (max-width:650px) and (orientation: portrait)": {
      flex: "3",
    },
  },
  modalText: {
    marginLeft: "20px",
    fontWeight: "300",
    marginTop: "0",
    whiteSpace: "nowrap",
    fontSize: "13px",
  },
  closeButtonContainer: {
    position: "relative",
    width: "80%",
  },
  closeModalButton: {
    position: "absolute",
    zIndex: "9",
    right: "5px",
    top: "5px",
    "& *": {
      fontSize: "10px",
      display: "inline-block",
    },
    "&:hover": {
      cursor: "pointer",
    },
  },
  // Right Sidebar Styles
  detailRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    minHeight: "50px",
    width: "100%",
    borderBottom: "1px solid #f5f5f5",
    "& b": {
      color: "#8F0339",
      marginLeft: "4px",
    },
    "& .MuiTextField-root": {
      width: "100%",
    },
  },
  icon: {
    width: "60px",
  },
  tag: {
    borderRadius: "3px",
    backgroundColor: "#f5f5f5",
    padding: "2px",
    margin: "3px",
    border: "none",
  },
  input: {
    "& *": {
      backgroundColor: "inherit !important",
    },
  },
  dialogText: {
    color: "#000000",
    fontWeight: "300",
    marginBottom: "10px",
    fontSize: "16px",
  },
  iconButtonADA: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  button: {
    paddingLeft: "10%",
    paddingRight: "10%",
    whiteSpace: "nowrap",
  },
  typeIcon: {
    fill: "none",
    opacity: ".5",
    width: "50%",
    height: "50%",
    "@media (max-width:960px)": {
      width: "50vw",
      height: "20vh",
    },
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "100%",
    userDrag: "none",
    userSelect: "none",
    MozUserSelect: "none",
    WebkitUserDrag: "none",
    WebkitUserSelect: "none",
    MsUserSelect: "none",
  },
  notEmbeddable: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
    color: "black",
  },
  popupCloseContainer: {
    position: "absolute",
    top: 5,
    right: 5,
  },
  popupClose: {
    display: "flex",
    alignSelf: "flex-end",
    margin: "5px",
    color: "#000",
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  shareButton: {
    bottom: 0,
    right: 0,
    padding: "8px 12px",
    color: "#fff",
    textAlign: "center",
    maxWidth: 125,
    marginBottom: "5px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "rgba(143,3,57,0.8)",
    },
  },
  linkPreview: {
    width: "100%",
    height: "100%",
    border: 0,
  },
}));

function ElementLinkDialog() {
  const classes = useStyles();

  const { elementLinkDialog = false } = useSelector(simpleValuesSelector);

  const { open } = elementLinkDialog;

  const { element } = useSelector(currentElementSelector);

  const theme = useTheme();
  const fullScreenSmall = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(
      setSimpleValue("elementLinkDialog", {
        elementId: null,
        url: null,
        open: false,
      }),
    );
  };

  const Content = ({ doc, classes }) => {
    const { type, url, xFrameOptions } = doc;
    if (type === ELEMENT_TYPE.LINK) {
      if (xFrameOptions !== "NONE") {
        return (
          <div className={classes.notEmbeddable}>
            <div>
              <LinkU />
              <Typography style={{ marginTop: "2em", maxWidth: 500, padding: "0 80px" }} color="primary" variant="h6">
                {`Sorry, this link won't let us include it inside the experience.
                Please open it in a new tab.`}
              </Typography>
              <Button
                className={classes.shareButton}
                variant="contained"
                color="primary"
                onClick={() => {
                  window.open(url, "_blank");
                }}
                size="large"
              >
                open link <Launch style={{ marginLeft: "5px" }} fontSize="small" />
              </Button>
            </div>
          </div>
        );
      }
      return <iframe className={classes.linkPreview} src={url} title={element.title} />;
    }
    return null;
  };

  const ContentMemo = memo(({ classes, doc, load }) => {
    return <Content classes={classes} doc={doc} load={load} />;
  });
  return (
    <Dialog
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      maxWidth={false}
      open={open}
      onClose={handleClose}
      fullScreen={fullScreenSmall}
    >
      <DialogContent className={classes.paper}>
        <div className={classes.mediaPreview}>
          <span className={classes.popupCloseContainer}>
            <IconButton size="small" className={classes.popupClose} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </span>
          <ContentMemo doc={element} classes={classes} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function areEqual(prevProps, nextProps) {
  const difference = objectDifference(nextProps, prevProps);
  return isEmpty(difference);
}

const MemoizedComponent = memo(ElementLinkDialog, areEqual);

export default MemoizedComponent;
